import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Box, Typography, styled } from "@mui/material";
import {
  BottomActionArea,
  BottomActionButton,
  ExtraWhiteSpace,
  FormBody,
  FormContentContainer,
  Header,
  NavigateBeforeButton,
  RadioInput,
  SingleFormInput,
  TextAreaFormInput,
  scrollToTop,
} from "../../components";
import { MedicineTreatmentForm } from "./components/MedicineTreatmentForm";
import { PrescriptionDetail, PrescriptionType } from "../../interfaces";
import { FormContainer } from "../../styles/styled";
import { theme as oldTheme } from "../../config";
import { useMedicineSearchContext, useDocumentFlow } from "@contexts";
import { usePrescriptionInteractions } from "@hooks/useInteractionAlerts";

export const MedicineDataContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  margin: "16px 0",
  padding: "16px 8px",
  backgroundColor: theme.palette.background.white,
  border: "1px solid #eaeaea",
  width: "100%",
}));

export const MedicineTitle = styled(Typography)({
  color: oldTheme.pallete.text.black,
  fontWeight: 500,
});
export const MedicineDetail = styled(Typography)({
  fontSize: "14px",
  color: "#747474",
});

interface PrescriptionDetailExtended extends PrescriptionDetail {
  prescriptionType: PrescriptionType;
}

export function AddMedicineManually() {
  const navigate = useNavigate();
  const location = useLocation();
  const flow = useDocumentFlow();
  const { searchedQuery = "" }: { searchedQuery: string } =
    location.state || {};

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<PrescriptionDetailExtended>({
    defaultValues: {
      activePrinciple: searchedQuery,
    },
  });
  const activePrinciple = watch("activePrinciple");
  const name = watch("name");
  const prescriptionType = watch("prescriptionType");

  const { searchResults, setSearchResults } = useMedicineSearchContext();

  const [settingMedicineData, setSettingMedicineData] = useState(true);
  const [settingTreatment, setSettingTreatment] = useState<boolean>(true);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const onMedicineDataSubmit = () => {
    setSettingMedicineData(false);
    scrollToTop();
  };

  const { handleRefetch } = usePrescriptionInteractions(
    flow.enableInteractions ? activePrinciple : "",
    flow.enableInteractions && !settingMedicineData,
  );

  const onSubmit = useCallback(
    async (formData: PrescriptionDetail) => {
      if (hasSubmitted) return;
      setHasSubmitted(true);
      try {
        setSettingTreatment(false);
        scrollToTop();

        const detail = {
          ...formData,
          prescriptionType: prescriptionType ?? undefined,
          posologyQuantity: Number(formData.posologyQuantity),
          frequencyQuantity: Number(formData.frequencyQuantity),
          durationQuantity: Number(formData.durationQuantity),
        };
        await flow.submitDetail(detail as PrescriptionDetail);
        await handleRefetch();
        setSearchResults(undefined);

        navigate(flow.routes.afterSubmitRoute, {
          state: {
            detailAdded: true,
            medicineType: prescriptionType === "HELD" ? "HELD" : "SIMPLE",
          },
        });
      } catch (error) {
        console.error("Error adding prescription detail: ", error);
      } finally {
        setHasSubmitted(false);
      }
    },
    [
      prescriptionType,
      setSearchResults,
      setHasSubmitted,
      handleRefetch,
      navigate,
      hasSubmitted,
      flow,
    ],
  );

  const handleBack = useCallback(() => {
    if (settingMedicineData) {
      navigate(flow.routes.treatmentBackRoute, {
        state: { searchResults },
      });
      return;
    } else if (settingTreatment) {
      setSettingMedicineData(true);
    } else {
      setSettingTreatment(true);
    }
    scrollToTop();
  }, [
    settingMedicineData,
    settingTreatment,
    flow.routes.treatmentBackRoute,
    searchResults,
    navigate,
  ]);

  return (
    <>
      <Header
        title="Agregar medicamento"
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          {settingMedicineData ? (
            <FormContainer onSubmit={handleSubmit(onMedicineDataSubmit)}>
              <SingleFormInput
                title="Medicamento"
                parameterName="activePrinciple"
                errorText="Debes ingresar un medicamento."
                register={register}
                errors={errors}
              />

              <TextAreaFormInput
                title="Especificaciones"
                parameterName="name"
                register={register}
                errors={errors}
                helperText="Ej: Dosis, forma farmacéutica, etc."
                minHeight="80px"
              />

              <RadioInput
                title="Tipo de receta"
                parameterName="prescriptionType"
                register={register}
                control={control}
                options={[
                  { value: "SIMPLE", label: "Simple" },
                  { value: "HELD", label: "Retenida" },
                ]}
                helperText="No esta permitido emitir recetas cheque digitales."
                errors={errors}
                errorText="Debes seleccionar el tipo de receta."
              />

              <ExtraWhiteSpace />

              <BottomActionArea>
                <BottomActionButton type="submit">Siguiente</BottomActionButton>
              </BottomActionArea>
            </FormContainer>
          ) : (
            <>
              {settingTreatment && (
                <MedicineDataContainer>
                  <MedicineTitle>{activePrinciple}</MedicineTitle>
                  {name && <MedicineDetail>{name}</MedicineDetail>}
                </MedicineDataContainer>
              )}
              <MedicineTreatmentForm
                prefilledMedicineAttributes={{
                  activePrinciple,
                  name,
                  prescriptionType,
                }}
                formManagement={{
                  onSubmit,
                  isLoading: hasSubmitted,
                }}
                submitButtonText={flow.labels.submitButtonText}
              />
            </>
          )}
        </FormBody>
      </FormContentContainer>
    </>
  );
}
